import {DateTime} from 'luxon';
import React, {FC, ReactNode, useMemo, useRef} from 'react';
import styled, {css} from 'styled-components';

import {BillingUpgradeDiamondIcon} from '../../../../../libs/shared/billing/web/src/components/billingUpgradeDiamondIcon';
import {BillingUpgradeIcon} from '../../../../../libs/shared/billing/web/src/components/billingUpgradeIcon';
import {
  calculateNextPeriods,
  DatePickerDirectionsEnum,
  DatePickerIconStyledProps,
} from '../../../../../libs/shared/core/src/helpers/datetime/dateTimeHelpers';
import {Trans} from '../../../../../libs/shared/core/src/helpers/internationalization/react';
import {UNLIMITED_FEATURE_COUNT} from '../../../../../libs/shared/core/src/helpers/models/billingHelpers';
import {
  StartAndEndDates,
  StartAndEndDatesWithPeriod,
} from '../../../../../libs/shared/core/src/helpers/models/dateIntervalModels';
import {CompanyFeaturesEnum} from '../../../../../libs/shared/core/src/models/api/company/companyModel';
import {InterfaceEventsEnum} from '../../../../../libs/shared/core/src/services/tracking/tracking';
import {AnalyticsPanelId} from '../../../../../libs/shared/core/src/store/panels/panelStateBase';
import {IconButton} from '../../../../../libs/shared/ui/src/components/buttons/iconButton';
import {DateRangeComboBox} from '../../../../../libs/shared/ui/src/components/datetime/dateRangeComboBox';
import {TooltipCoordinator} from '../../../../../libs/shared/ui/src/components/tooltips/tooltipCoordinator';
import {useUserTheme} from '../../../../../libs/shared/ui/src/theme/userThemeProvider';
import {useAppDispatch, useAppSelector} from '../../../../helpers/appReduxHelpers';
import {getCalendarWeekStartDay} from '../../../../store/data/preferences/preferencesSelectors';
import {getUnsavedViewNavigationBlockerId} from '../../../../store/panels/analytics/analyticsPanelSelectors';
import {
  getAnalyticsDatetimes,
  makeGetAnalyticsParameters,
} from '../../../../store/panels/analytics/parameters/analyticsPanelParametersSelectors';
import {navigateToLinkAction} from '../../../../store/stateless/navigationActions';
import {ANALYTICS_TIME_PERIODS} from '../../../helpers/analyticsFiltersHelpers';
import {makeAnalyticsDateTimesUrl} from '../../../helpers/analyticsUrlHelpers';
import {getMaxNumAnalyticsRetentionDays} from '../../../helpers/tracking/analyticsSelectors';
import {useAnalyticsTracking} from '../../../helpers/tracking/useAnalyticsTracking';
import {analyticsFilterColor} from '../../filters/common/helpers';
import {AnalyticsBillingUpgradeTooltip} from '../../reportElements/tooltips/analyticsBillingUpgradeTooltip';

/*
 * Style.
 */

const StyledDiamondGridItemDiv = styled.div`
  opacity: 0.5;
  display: flex;
  justify-content: center;
`;

const DatePickerToggleIconSharedStyled = css`
  height: 30px;
  padding-top: 6.5px;
`;

const DatePickerStyledLeftToggleIconButton = styled(IconButton)`
  ${DatePickerToggleIconSharedStyled};
  border-top-right-radius: 0px;
  border-bottom-right-radius: 0px;
  border-right: 1px solid ${(p) => p.theme.greys.shade40};
`;

const DatePickerIconSharedStyles = (props: DatePickerIconStyledProps) => css`
  transform: rotate(${props.$rotate});
  margin: ${props.$margin};
  color: ${props.$color || ((p) => p.theme.greys.shade70)};
`;

const DatePickerStyledIconButton = styled(IconButton)<DatePickerIconStyledProps>`
  ${DatePickerIconSharedStyles}
`;

/*
 * Props.
 */

interface ReportHeaderDatepickerProps {
  color?: string;
  panelId: AnalyticsPanelId;
}

export const ReportHeaderDatepicker: FC<ReportHeaderDatepickerProps> = ({color, panelId}) => {
  const getPanelId = useMemo(() => () => panelId, [panelId]);
  const dispatch = useAppDispatch();
  const trackAnalytics = useAnalyticsTracking();
  const [startDate, endDate] = useAppSelector(getAnalyticsDatetimes);
  const parameters = useAppSelector(makeGetAnalyticsParameters(getPanelId));
  const calendarWeekStartDay = useAppSelector(getCalendarWeekStartDay);
  const unsavedViewNavigationBlockerId = useAppSelector(getUnsavedViewNavigationBlockerId);
  const analyticsBillingUpgradeTooltipRef = useRef<HTMLDivElement>(null);
  const analyticsRetentionDays = useAppSelector(getMaxNumAnalyticsRetentionDays);
  const minDate = getMinDate(analyticsRetentionDays);
  const theme = useUserTheme();
  const shouldShowUpsellTooltip =
    analyticsRetentionDays === ANALYTICS_TIME_PERIODS[CompanyFeaturesEnum.MEDIUM_ANALYTICS_TIME_RANGE];
  const previousButtonIcon = shouldShowUpsellTooltip ? 'diamondSmall' : 'chevronLeftSmall';
  const previousButtonIconColor = shouldShowUpsellTooltip
    ? theme.brandPalette.violets.shade500
    : theme.greys.shade50;

  const onDateSelect = async (dates: StartAndEndDatesWithPeriod) => {
    trackAnalytics(InterfaceEventsEnum.ANALYTICS_DATE_RANGE_SELECT, {
      start_date: dates.startDate?.toISO(),
      end_date: dates.endDate?.toISO(),
      period: dates.period ?? 'custom',
    });

    if (!hasAllDates(dates)) {
      return;
    }
    // If dates changed, we need to update report data.
    if (
      (dates.startDate && !startDate?.equals(dates.startDate)) ||
      (dates.endDate && !endDate?.equals(dates.endDate))
    ) {
      const from = dates.startDate.toMillis();
      const to = dates.endDate.toMillis();
      // We should ignore blocking navigation for report type and dates changes
      // because they are not stored in views.
      const path = makeAnalyticsDateTimesUrl(from, to, parameters);
      await dispatch(navigateToLinkAction(path, undefined, unsavedViewNavigationBlockerId));
    }
  };

  const analyticsBillingUpgradeTooltipWrapperRenderer = (children: ReactNode, isItemDisabled: boolean) => (
    <AnalyticsBillingUpgradeTooltip
      isItemDisabled={isItemDisabled}
      analyticsBillingUpgradeTooltipRef={analyticsBillingUpgradeTooltipRef}
    >
      {children}
    </AnalyticsBillingUpgradeTooltip>
  );

  const basicTooltipWrapperRenderer = (children: ReactNode) => (
    <TooltipCoordinator
      placement="bottom"
      render={() => (
        <Trans id="analytics-access-restricted">Can&lsquo;t view analytics beyond the last 2 years.</Trans>
      )}
    >
      {children}
    </TooltipCoordinator>
  );

  const onOpen = () => {
    trackAnalytics(InterfaceEventsEnum.ANALYTICS_DATE_RANGE_CLICK);
  };

  const disabledIcon = (
    <StyledDiamondGridItemDiv>
      <BillingUpgradeIcon />
    </StyledDiamondGridItemDiv>
  );

  // Get time range access for the current period.
  const checkTimePeriodAccess = (timeRangeStartDate: StartAndEndDatesWithPeriod | undefined) => {
    if (analyticsRetentionDays === UNLIMITED_FEATURE_COUNT) {
      return true;
    }

    if (analyticsRetentionDays === 0) {
      return false;
    }

    const limit = DateTime.now().startOf('day').minus({days: analyticsRetentionDays}).toSeconds();

    const startSeconds = timeRangeStartDate?.startDate?.toSeconds();

    return startSeconds && startSeconds >= limit;
  };

  const hasAccessToPreviousPeriod = () => {
    if (!startDate || !endDate) {
      return false;
    }
    const previousPeriod = calculateNextPeriods(startDate, endDate, DatePickerDirectionsEnum.BACKWARD);
    return checkTimePeriodAccess(previousPeriod);
  };

  const renderPreviousIconToggleButton = (isHover: boolean) => {
    if (!hasAccessToPreviousPeriod() && isHover) {
      const billingUpgradeIconButton = (
        <DatePickerStyledLeftToggleIconButton
          iconName={previousButtonIcon}
          label=""
          hoverColor={previousButtonIconColor}
          backgroundColor={theme.greys.shade30}
        />
      );
      if (shouldShowUpsellTooltip) {
        return analyticsBillingUpgradeTooltipWrapperRenderer(billingUpgradeIconButton, true);
      }
      return basicTooltipWrapperRenderer(billingUpgradeIconButton);
    }
    return null;
  };

  const renderPreviousIconButton = (isHover: boolean) => {
    if (!hasAccessToPreviousPeriod() && isHover) {
      const billingUpgradeIconButton = (
        <DatePickerStyledIconButton
          iconName={previousButtonIcon}
          label=""
          hoverColor={previousButtonIconColor}
        />
      );
      if (shouldShowUpsellTooltip) {
        return analyticsBillingUpgradeTooltipWrapperRenderer(billingUpgradeIconButton, true);
      }
      return basicTooltipWrapperRenderer(billingUpgradeIconButton);
    }
    return null;
  };

  const isActive = Boolean(startDate && endDate);
  const iconColor = isActive ? analyticsFilterColor.findActive(theme) : analyticsFilterColor.findBase(theme);
  const isPreviousButtonDisabled = !hasAccessToPreviousPeriod();
  return (
    <DateRangeComboBox
      selectedStartDate={startDate}
      selectedEndDate={endDate}
      calendarWeekStartDay={calendarWeekStartDay}
      onDateSelect={onDateSelect}
      onOpen={onOpen}
      dateItemWrapperRenderer={
        shouldShowUpsellTooltip ? analyticsBillingUpgradeTooltipWrapperRenderer : basicTooltipWrapperRenderer
      }
      disabledIcon={shouldShowUpsellTooltip ? disabledIcon : null}
      ref={analyticsBillingUpgradeTooltipRef}
      periodSelectionChevronsInButton
      checkTimePeriodAccess={checkTimePeriodAccess}
      minDate={minDate}
      color={color}
      iconColor={iconColor}
      isActive={isActive}
      renderTimePeriodUnavailableIndicator={() => <BillingUpgradeDiamondIcon />}
      isPreviousButtonDisabled={isPreviousButtonDisabled}
      renderPreviousIconToggleButton={renderPreviousIconToggleButton}
      renderPreviousIconButton={renderPreviousIconButton}
    />
  );
};

/*
 * Helpers.
 */

function hasAllDates(dates: StartAndEndDates): dates is Required<StartAndEndDates> {
  return Boolean(dates.startDate && dates.endDate);
}

function getMinDate(timeRangeFeatureDays?: number) {
  if (timeRangeFeatureDays && timeRangeFeatureDays > 0) {
    return DateTime.now().startOf('day').minus({days: timeRangeFeatureDays});
  }

  return undefined;
}
